import type { ReactNode } from 'react';
import React from 'react';
import { useRouter } from 'next/router';
import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import Styles from './ConstructionItemSettingList.module.scss';

import { ConstructionItemSettingListRow } from './ConstructionItemSettingListRow';
import { useConstructionItems } from '~/hooks/vendors/useConstructionItems';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import type { ConstructionItemSettingEdit } from '~/models/ConstructionItemSetting';
import { validationSchemaConstructionItemSetting } from '~/models/ConstructionItemSetting';
import { PATH as COMPANY_SETTING_PATH } from '~/pages/vendors/settings/company_settings';
import { create as createSetting } from '~/utils/api/vendors/ConstructionItemSetting';
import { useConstructionItemSettings } from '~/hooks/vendors/useConstructionItemSettings';
import { isSuccess } from '~/utils/api/api_handler';
import { useCompanySettings } from '~/hooks/vendors/useCompanySettings';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';

export const ConstructionItemSettingList: React.FC = () => {
  const {
    isOpen: confirmOpen,
    handleOpen: handleConfirmOpen,
    handleClose: handleConfirmClose
  } = useOpenHandler();
  const [update, setUpdate] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const router = useRouter();
  const { data: constructionItems } = useConstructionItems().index();
  const companyMasterId = useIdQueryHandler('companyMasterId');
  const { data: constructionItemSettings, mutate: mutateItemSettings } =
    useConstructionItemSettings().index(companyMasterId);
  const { data } = useCompanySettings().index('', null);
  const { data: companySetting } = useCompanySettings().show(companyMasterId);
  if (
    constructionItemSettings === null ||
    constructionItems === null ||
    data === null ||
    companySetting === null
  ) {
    return null;
  }
  const handleSave = async (
    submitValues: ConstructionItemSettingEdit[]
  ): Promise<void> => {
    setSubmitting(true);
    if (companyMasterId === null) {
      return;
    }
    if (submitValues.length === 0) {
      await router.push(COMPANY_SETTING_PATH);
      toast.success(`保存しました`);
      return;
    }
    const result = await createSetting(submitValues, companyMasterId);
    if (isSuccess(result)) {
      await mutateItemSettings?.();
      await router.push(COMPANY_SETTING_PATH);
      toast.success('保存しました');
      return;
    }
    setSubmitting(false);
    toast.error(`エラーが発生しました。${result.message}`);
  };
  return (
    <Formik
      initialValues={constructionItemSettings}
      onSubmit={handleSave}
      validationSchema={validationSchemaConstructionItemSetting}
    >
      {({
        values,
        errors,
        handleReset,
        setValues,
        handleSubmit
      }: FormikProps<ConstructionItemSettingEdit[]>): ReactNode => (
        <div>
          <div className={Styles.SettingList}>
            <div className={Styles.TitleList}>
              <h1 className={Styles.CompanyName}>{companySetting.name}</h1>
              <h2 className={Styles.SettingTitle}>工事単価一覧</h2>
            </div>
            <p className={Styles.Button}>
              {update ? (
                <>
                  <button
                    className={clsx(Styles.CancelButton, Styles.EditButton)}
                    onClick={(): void => {
                      handleReset();
                      setUpdate(false);
                    }}
                    type="button"
                  >
                    キャンセル
                  </button>
                  <button
                    className={clsx(Styles.SubmitButton)}
                    onClick={handleConfirmOpen}
                    type="button"
                  >
                    更新
                  </button>
                </>
              ) : (
                <button
                  className={Styles.EditButton}
                  onClick={(): void => {
                    setUpdate(true);
                  }}
                  type="button"
                >
                  編集
                </button>
              )}
            </p>
          </div>
          <table className={Styles.List}>
            <thead>
              <tr className={clsx(Styles.ListRow, Styles.Header)}>
                <th>項目名</th>
                <th>単価</th>
              </tr>
            </thead>
            <tbody>
              {constructionItems.map((item) => (
                <ConstructionItemSettingListRow
                  key={item.id}
                  constructionItem={item}
                  errors={errors}
                  setValues={setValues}
                  showUpdate={update}
                  values={values}
                />
              ))}
            </tbody>
          </table>
          <ConfirmModal
            actionText="更新"
            confirmType="save"
            disabled={submitting}
            handleAccept={handleSubmit}
            handleCancel={handleConfirmClose}
            isOpen={confirmOpen}
          >
            単価を更新してよろしいですか？
          </ConfirmModal>
        </div>
      )}
    </Formik>
  );
};
